/**
 * Les feature flags (docs/05 §11, docs/16 §2).
 *
 * **Un écran qu'on ouvre pendant un incident**, et il est écrit pour cela :
 *
 * - la **description est aussi visible que l'interrupteur**. `messaging.enabled`
 *   ne dit pas ce qui s'éteint ; un opérateur qui ne le sait pas n'ose rien
 *   toucher, et un coupe-circuit qu'on n'ose pas actionner n'en est pas un.
 * - **couper demande une confirmation, rouvrir non.** L'asymétrie est
 *   délibérée : fermer une fonctionnalité pour toute la plateforme se fait
 *   rarement et se regrette ; la rouvrir est le geste de réparation, et lui
 *   mettre une porte ferait perdre les secondes où l'on veut aller vite.
 * - **aucune création, aucune suppression.** Un flag naît dans le code, avec le
 *   `if` qui le consulte. En inventer un ici produirait une ligne que rien ne
 *   lit, qu'aucun signe ne distinguerait d'un vrai.
 *
 * Les `rules` sont **affichées et non éditables** ici. Un éditeur de JSON libre
 * dans un écran d'incident est la meilleure façon d'écrire une règle fausse à
 * trois heures du matin ; le déploiement progressif se règle en base, avec le
 * temps d'y penser, et se **lit** ici pour comprendre pourquoi tout le monde ne
 * voit pas la même chose.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  useFeatureFlags,
  useSetFeatureFlag,
  type FeatureFlag,
} from "@/features/admin/api/useAdmin";
import { Button } from "@/shared/ui/Button";
import { Card } from "@/shared/ui/Card";
import { ErrorState } from "@/shared/ui/ErrorState";
import { Skeleton } from "@/shared/ui/Skeleton";

export function AdminFeatureFlagsPage() {
  const { t, i18n } = useTranslation("admin");
  const flags = useFeatureFlags();
  const set = useSetFeatureFlag();
  const [closing, setClosing] = useState<string | null>(null);

  const formatDateTime = (value: string) =>
    new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium", timeStyle: "short" }).format(
      new Date(value),
    );

  if (flags.isPending) {
    return <Skeleton className="my-8 h-64 w-full" label={t("flags.title")} />;
  }
  if (flags.isError) {
    return <ErrorState error={flags.error} />;
  }

  const flip = (flag: FeatureFlag, enabled: boolean) => {
    set.mutate({ key: flag.key, enabled, rules: flag.rules });
    setClosing(null);
  };

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-semibold">{t("flags.title")}</h1>
        <p className="text-sm text-slate-600 dark:text-slate-400">{t("flags.subtitle")}</p>
      </header>

      {set.isError && <ErrorState error={set.error} />}

      <ul className="space-y-4">
        {flags.data.map((flag) => (
          <li key={flag.key}>
            <Card>
              <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div className="space-y-1">
                  <p className="font-mono text-sm font-semibold">{flag.key}</p>
                  <p className="text-sm text-slate-600 dark:text-slate-400">
                    {flag.description ?? t("flags.noDescription")}
                  </p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">
                    {flag.enabled ? t("flags.open") : t("flags.closed")} ·{" "}
                    {t("flags.updatedAt", { at: formatDateTime(flag.updated_at) })}
                  </p>
                  {Object.keys(flag.rules).length > 0 && (
                    // Affichées telles quelles : ce sont elles qui expliquent
                    // qu'un flag « ouvert » ne le soit pas pour tout le monde.
                    <pre className="mt-2 overflow-x-auto rounded bg-slate-100 p-2 text-xs dark:bg-slate-800">
                      {JSON.stringify(flag.rules)}
                    </pre>
                  )}
                </div>

                <div className="shrink-0">
                  {closing === flag.key ? (
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        {t("flags.confirmClose")}
                      </p>
                      <Button
                        type="button"
                        variant="danger"
                        onClick={() => {
                          flip(flag, false);
                        }}
                      >
                        {t("flags.close")}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        onClick={() => {
                          setClosing(null);
                        }}
                      >
                        {t("flags.cancel")}
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="button"
                      variant={flag.enabled ? "secondary" : "primary"}
                      disabled={set.isPending}
                      onClick={() => {
                        // Couper demande une confirmation, rouvrir non : la
                        // seconde est le geste de réparation.
                        if (flag.enabled) {
                          setClosing(flag.key);
                        } else {
                          flip(flag, true);
                        }
                      }}
                    >
                      {flag.enabled ? t("flags.close") : t("flags.open_action")}
                    </Button>
                  )}
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ul>
    </div>
  );
}
